"use client";

// import { ModeToggle } from "./mode-toggle";
import { MobileNav } from "./mobile-nav";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { BookOpen, LogOut, Settings, User } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { useAuth } from "./contexts/auth-context";
import { SubjectsDataWithSlugs } from "../../pages/courses/SubjectsSection";

export function SiteHeader() {
	const { user, isAuthenticated, logout } = useAuth();

	// Get initials from user's name
	const getInitials = () => {
		if (!user) return "?";
		return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
	};

	return (
		<header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
			<div className="container flex h-16 items-center justify-between">
				<div className="flex items-center gap-2">
					<Link to="/" className="w-10 space-x-2 header__logo-link">
						<img
							src="/ighota-logo.png"
							alt="ighota-logo"
							draggable={false}
							className="header__logo"
						/>
					</Link>
				</div>
				<nav className="hidden md:flex md:gap-6 lg:gap-10">
					{/* About Us Dropdown */}
					<DropdownMenu>
						<DropdownMenuTrigger className="text-md font-medium transition-colors hover:text-primary focus:outline-none">
							About Us
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-48 p-1 bg-background border rounded-lg shadow-lg">
							<DropdownMenuItem
								asChild
								className="py-2 px-3 hover:bg-muted/50 hover:text-primary transition-colors rounded-md">
								<Link to="/team">Our Team</Link>
							</DropdownMenuItem>
							<DropdownMenuItem
								asChild
								className="py-2 px-3 hover:bg-muted/50 hover:text-primary transition-colors rounded-md">
								<Link to="/mission">Our Mission</Link>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>

					{/* Courses Dropdown */}
					<DropdownMenu>
						<DropdownMenuTrigger className="text-md font-medium transition-colors hover:text-primary focus:outline-none">
							Courses
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-[750px] p-0 bg-background border rounded-lg shadow-lg">
							<div className="grid grid-cols-3 gap-0">
								{SubjectsDataWithSlugs.map((subject, index) => (
									<div
										key={index}
										className="p-4 hover:bg-muted/50 transition-colors">
										<h4 className="font-bold mb-2 text-primary text-lg">
											{subject.name}
										</h4>
										<ul className="space-y-1.5">
											{subject.subSubjects?.map(
												(subSubject, subIndex) => (
													<li
														key={`${index}-${subIndex}`}>
														<Link
															to={`/courses/${subject.slug}/${subSubject.slug}`}
															className="text-sm text-muted-foreground hover:text-primary transition-colors block py-1">
															{subSubject.name}
														</Link>
													</li>
												)
											)}
										</ul>
									</div>
								))}
							</div>
							<div className="border-t p-3 bg-muted/20">
								<Link
									to="/courses"
									className="text-xs text-primary hover:underline flex items-center justify-end">
									View all courses
								</Link>
							</div>
						</DropdownMenuContent>
					</DropdownMenu>

					<Link
						to="/partners"
						className="text-md font-medium transition-colors hover:text-primary">
						Partners
					</Link>
					<Link
						to="/subscription"
						className="text-md font-medium transition-colors hover:text-primary">
						Pricing
					</Link>
					<Link
						to="/contact"
						className="text-md font-medium transition-colors hover:text-primary">
						Contact Us
					</Link>
					<Link
						to="https://www.gofundme.com/f/empower-nigerian-kids-with-virtual-learning-join-us"
						className="text-md font-medium transition-colors hover:text-primary">
						Donate
					</Link>
				</nav>
				<div className="flex items-center gap-4">
					{/* <ModeToggle /> */}

					{isAuthenticated ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								{/* <Button
									variant="ghost"
									className="relative h-8 w-8 rounded-full"> */}
								<button>
									<Avatar className="h-8 w-8">
										<AvatarFallback>
											{getInitials()}
										</AvatarFallback>
									</Avatar>
								</button>
								{/* </Button> */}
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuLabel>
									My Account
								</DropdownMenuLabel>
								<DropdownMenuSeparator />
								<DropdownMenuItem asChild>
									<Link
										to="/dashboard"
										className="cursor-pointer flex w-full items-center">
										<BookOpen className="mr-2 h-4 w-4" />
										<span>Dashboard</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										to="/profile"
										className="cursor-pointer flex w-full items-center">
										<User className="mr-2 h-4 w-4" />
										<span>Profile</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										to="/settings"
										className="cursor-pointer flex w-full items-center">
										<Settings className="mr-2 h-4 w-4" />
										<span>Settings</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={logout}
									className="cursor-pointer">
									<LogOut className="mr-2 h-4 w-4" />
									<span>Log out</span>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<div className="hidden md:flex md:gap-2">
							<Button variant="outline" asChild>
								<Link to="/auth/login">Login</Link>
							</Button>
							<Button
								className="bg-yellow-500 hover:bg-yellow-700 transition-all duration-300 text-white font-bold"
								asChild>
								<Link to="/benchmark-test">
									Take Benchmark Test
								</Link>
							</Button>
						</div>
					)}
					<MobileNav />
				</div>
			</div>
		</header>
	);
}
